import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent, CircularProgress } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './FinalizeAllDraftsDialog.module.scss'

// SSE consumer mirrors OpsBar.consumeSseStream — duplicated here to keep this
// dialog self-contained. The OpsBar version is exhaustively tested; extracting
// to a shared util can wait for a third caller.
async function consumeSseStream(response, onEvent) {
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const processFrame = (frame) => {
    for (const line of frame.split('\n')) {
      if (line.startsWith('data:')) {
        const json = line.slice(5).trim()
        if (json) {
          try {
            onEvent(JSON.parse(json))
          } catch (err) {
            logger.error('Bad SSE frame', err, json)
          }
        }
      }
    }
  }

  const drain = () => {
    let sep
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      processFrame(frame)
    }
  }

  try {
    let streaming = true
    while (streaming) {
      const { value, done } = await reader.read()
      if (done) {
        streaming = false
        break
      }
      buffer += decoder.decode(value, { stream: true })
      drain()
    }
    buffer += decoder.decode()
    drain()
    if (buffer.trim()) processFrame(buffer)
  } finally {
    try {
      reader.cancel().catch(() => {})
    } catch (_err) {
      /* reader already released */
    }
  }
}

const formatCurrency = (minorAmount, currency = 'USD') => {
  if (minorAmount == null) return '—'
  const amount = (minorAmount || 0) / 100
  return new window.Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
}

const CHARGE_LABELS = {
  charged: { text: 'Charged', tone: 'ok' },
  queue_manual_ach: { text: 'Queued for manual ACH', tone: 'warn' },
  no_eligible_pm: { text: 'No eligible PM', tone: 'warn' },
  charge_failed: { text: 'Charge failed', tone: 'err' },
  skipped_no_stripe_ids: { text: 'No Stripe IDs', tone: 'warn' },
  skipped_pm_lookup_failed: { text: 'PM lookup failed', tone: 'err' },
  invoice_not_found: { text: 'Not found', tone: 'err' },
  error: { text: 'Charge error', tone: 'err' },
}

const StatusPill = ({ status, chargeAction, error }) => {
  if (!status) {
    return (
      <span className={`${styles.pill} ${styles.pillPending}`}>
        <FontAwesomeIcon icon="circle-notch" spin /> queued
      </span>
    )
  }
  if (status === 'failed' || error) {
    return (
      <span className={`${styles.pill} ${styles.pillErr}`}>
        <FontAwesomeIcon icon="exclamation-triangle" /> failed
      </span>
    )
  }
  const meta = CHARGE_LABELS[chargeAction] || { text: 'Finalized', tone: 'ok' }
  const cls =
    meta.tone === 'ok' ? styles.pillOk : meta.tone === 'warn' ? styles.pillWarn : styles.pillErr
  return (
    <span className={`${styles.pill} ${cls}`}>
      <FontAwesomeIcon icon={meta.tone === 'ok' ? 'check-circle' : 'exclamation-triangle'} />{' '}
      {meta.text}
    </span>
  )
}
StatusPill.propTypes = {
  status: PropTypes.string,
  chargeAction: PropTypes.string,
  error: PropTypes.string,
}

const FinalizeAllDraftsDialog = ({ open, draftCount, onClose, onComplete, showToast }) => {
  const [phase, setPhase] = useState('confirm') // 'confirm' | 'running' | 'done'
  const [progress, setProgress] = useState({ processed: 0, total: draftCount || 0 })
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState(null)

  const handleClose = useCallback(() => {
    if (phase === 'running') return
    setPhase('confirm')
    setRows([])
    setProgress({ processed: 0, total: draftCount || 0 })
    setSummary(null)
    onClose()
  }, [phase, draftCount, onClose])

  const runFinalize = useCallback(async () => {
    setPhase('running')
    setRows([])
    setSummary(null)
    setProgress({ processed: 0, total: draftCount || 0 })
    try {
      const response = await authenticatedFetch(
        `${apiEndpoints.billingInvoices}/approve-all-stream`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify({}),
        },
      )
      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '')
        throw new Error(text || `Finalize failed (${response.status})`)
      }

      let done = null
      await consumeSseStream(response, (event) => {
        if (event.type === 'start') {
          setProgress({ processed: 0, total: event.total })
        } else if (event.type === 'row') {
          setProgress({ processed: event.processed, total: event.total })
          setRows((prev) => [...prev, event])
        } else if (event.type === 'done') {
          done = event
        } else if (event.type === 'error') {
          throw new Error(event.error || 'Stream error')
        }
      })

      if (done) {
        setSummary(done)
        const parts = [`Finalized ${done.approved} / ${done.total}`]
        if (done.failed) parts.push(`${done.failed} failed`)
        const severity = done.failed ? 'warning' : 'success'
        showToast(parts.join(' — '), severity)
        onComplete()
      } else {
        showToast('Finalize ended without a "done" event — check server logs', 'warning')
      }
    } catch (err) {
      logger.error('approve-all-stream failed', err)
      showToast(err.message || 'Finalize failed', 'error')
    } finally {
      setPhase('done')
    }
  }, [draftCount, showToast, onComplete])

  const counts = rows.reduce(
    (acc, r) => {
      if (r.status === 'failed') acc.failed++
      else if (r.charge_action === 'charged') acc.charged++
      else if (r.charge_action === 'queue_manual_ach') acc.queued++
      else if (r.charge_action === 'no_eligible_pm') acc.noPm++
      else if (r.charge_action === 'charge_failed') acc.chargeFailed++
      else acc.other++
      return acc
    },
    { charged: 0, queued: 0, noPm: 0, chargeFailed: 0, failed: 0, other: 0 },
  )

  return (
    <Dialog
      open={open}
      onClose={phase === 'running' ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      data-testid="finalize-all-dialog"
    >
      <DialogContent className={styles.dialogContent}>
        <h3 className={styles.title}>Finalize All Drafts</h3>

        {phase === 'confirm' && (
          <>
            <p className={styles.copy}>
              About to finalize <strong>{draftCount}</strong> draft invoice
              {draftCount === 1 ? '' : 's'}. For each invoice this will:
            </p>
            <ul className={styles.bulletList}>
              <li>
                Transition the local status from <code>draft</code> &rarr; <code>pending</code>
              </li>
              <li>
                Finalize the Stripe draft (becomes <code>open</code>)
              </li>
              <li>Send the invoice email to the customer</li>
              <li>
                Auto-charge the customer&apos;s preferred payment method (cards / PAD), or queue
                manual ACH for US bank rails
              </li>
            </ul>
            <p className={styles.warning}>
              <FontAwesomeIcon icon="exclamation-triangle" /> This will move real money via Stripe.
              Make sure each draft has been reviewed in the Stripe dashboard.
            </p>
            <div className={styles.actions}>
              <button type="button" className={styles.btnSecondary} onClick={handleClose}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={runFinalize}
                data-testid="finalize-all-confirm"
                disabled={!draftCount}
              >
                Finalize {draftCount} Draft{draftCount === 1 ? '' : 's'}
              </button>
            </div>
          </>
        )}

        {(phase === 'running' || phase === 'done') && (
          <>
            <div
              className={styles.progressWrapper}
              role="progressbar"
              aria-valuenow={progress.processed}
              aria-valuemin={0}
              aria-valuemax={progress.total}
            >
              <div className={styles.progressLabel}>
                {phase === 'running'
                  ? `Processing ${progress.processed} / ${progress.total}…`
                  : `Done — ${progress.processed} / ${progress.total}`}
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{
                    width: progress.total
                      ? `${Math.round((progress.processed / progress.total) * 100)}%`
                      : '0%',
                  }}
                />
              </div>
            </div>

            {phase === 'done' && summary && (
              <div className={styles.summaryGrid}>
                <div className={`${styles.summaryCell} ${styles.summaryOk}`}>
                  <span className={styles.summaryNum}>{counts.charged}</span>
                  <span className={styles.summaryLbl}>Charged</span>
                </div>
                <div className={`${styles.summaryCell} ${styles.summaryWarn}`}>
                  <span className={styles.summaryNum}>{counts.queued}</span>
                  <span className={styles.summaryLbl}>Queued ACH</span>
                </div>
                <div className={`${styles.summaryCell} ${styles.summaryWarn}`}>
                  <span className={styles.summaryNum}>{counts.noPm}</span>
                  <span className={styles.summaryLbl}>No eligible PM</span>
                </div>
                <div className={`${styles.summaryCell} ${styles.summaryErr}`}>
                  <span className={styles.summaryNum}>{counts.chargeFailed + counts.failed}</span>
                  <span className={styles.summaryLbl}>Failed</span>
                </div>
              </div>
            )}

            <table className={styles.resultsTable}>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && phase === 'running' && (
                  <tr>
                    <td colSpan={6} className={styles.emptyRow}>
                      <CircularProgress size={14} style={{ marginRight: 8 }} />
                      Waiting for first row…
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.invoice_id}>
                    <td>{r.invoice_number || `#${r.invoice_id}`}</td>
                    <td>{r.account_name || '—'}</td>
                    <td>{r.invoice_type}</td>
                    <td>
                      <StatusPill
                        status={r.status}
                        chargeAction={r.charge_action}
                        error={r.error}
                      />
                    </td>
                    <td>
                      {r.amount_charged_minor != null
                        ? formatCurrency(r.amount_charged_minor, r.currency)
                        : '—'}
                    </td>
                    <td className={styles.detailCell}>
                      {r.error ||
                        (r.charge_action === 'no_eligible_pm'
                          ? 'Customer has no chargeable PM on file'
                          : r.charge_action === 'queue_manual_ach'
                            ? 'Manual ACH file required for collection'
                            : r.charge_action === 'charge_failed'
                              ? 'Stripe rejected charge — see audit log'
                              : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleClose}
                disabled={phase === 'running'}
                data-testid="finalize-all-close"
              >
                {phase === 'running' ? 'Working…' : 'Close'}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

FinalizeAllDraftsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  draftCount: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
}

export default FinalizeAllDraftsDialog
